// Premios de promos de referido en el cajón: totales de los dos lados, la franja de cada referido y
// las etiquetas de «Llegó recomendado por». Solo texto; la pestaña lo pinta.
import type {
    BackofficeCustomerReferral,
    BackofficeReferralReward,
} from '@/api/services/retail-wizard/customerReferrals.client';
import { formatDateMx, formatShortDateMx } from '@/lib/dateHelpers';
import { capitalizeFirst } from '@/shared/lib/format/customerMention';
import { formatMoneyCents } from '@/shared/lib/format/money';
import type { PillTone } from '@/shared/patterns/Pills';

type RewardStatus = BackofficeReferralReward['status'];
type RewardRole = BackofficeReferralReward['role'];
type RewardType = BackofficeReferralReward['rewardType'];

export const REWARD_STATUS_LABEL: Record<RewardStatus, string> = {
    PAID: 'Pagado',
    PENDING: 'Pendiente',
    NOT_APPLICABLE: 'No aplica',
};

const REWARD_STATUS_TONE: Record<RewardStatus, PillTone> = {
    PAID: 'success',
    PENDING: 'warning',
    NOT_APPLICABLE: 'neutral',
};

/** Los bonos en dinero son los únicos que se depositan a la cuenta PCF. */
const PCF_REWARD_TYPES: ReadonlySet<RewardType> = new Set(['FIXED_BONUS', 'PERCENT_BONUS']);

const MINUS = '−';

export const REWARDS_COPY = {
    noneLabel: 'Sin premio todavía',
    unavailableLabel: 'Premio no disponible',
    unavailableHint:
        'No pudimos consultar las promociones en este momento. No significa que no tenga premio.',
    referredNone: 'Sin premio todavía por esta recomendación.',
    referredUnavailable:
        'Por ahora no podemos ver qué premios dejó esta recomendación. Intenta de nuevo más tarde.',
    sideEmpty: 'Sin premio en esta promo',
    earnedTitle: (name: string) => `Lo que ganó ${name}`,
    givenTitle: 'Lo que ganaron sus referidos',
    earnedCount: (n: number) =>
        n === 1 ? '1 bono en su cuenta PCF' : `${String(n)} bonos en su cuenta PCF`,
    earnedZero: 'Todavía sin bonos pagados',
    givenCount: (n: number) => (n === 1 ? '1 premio aplicado' : `${String(n)} premios aplicados`),
    givenZero: 'Todavía sin premios aplicados',
    sideTitle: (name: string) => capitalizeFirst(`${name} ganó`),
    promotions: (names: readonly string[]) =>
        names.length === 1
            ? `Promo «${names[0]}»`
            : `Promos ${names.map((n) => `«${n}»`).join(', ')}`,
} as const;

/** Un premio de la franja: monto grande y una línea tenue con cómo y cuándo. */
export interface RewardItem {
    key: string;
    promotionName: string;
    amount: string;
    tone: 'success' | 'muted';
    detail: string;
}

/** `unavailable`: promociones no respondió; `none`: todavía no hay premio de ningún lado. */
export type ReferralRewardsView =
    | { kind: 'unavailable' }
    | { kind: 'none' }
    | { kind: 'split'; referrer: RewardItem[]; referred: RewardItem[]; promotions: string[] };

export interface RewardTag {
    key: string;
    text: string;
    tone: PillTone;
    title: string;
}

export type ReferredByRewardsView =
    | { kind: 'unavailable' }
    | { kind: 'none' }
    | { kind: 'tags'; tags: RewardTag[] };

export type RewardTotals =
    | { kind: 'unavailable' }
    | {
          kind: 'ready';
          earnedCents: number;
          earnedCount: number;
          givenCents: number;
          givenCount: number;
      };

const money = (cents: number | null) => (cents === null ? null : formatMoneyCents(cents));

const byPromotionName = (a: BackofficeReferralReward, b: BackofficeReferralReward) =>
    a.promotionName.localeCompare(b.promotionName, 'es', { sensitivity: 'base' });

const keyOf = (r: BackofficeReferralReward) => `${r.promotionId}-${r.role}-${r.rewardType}`;

function rolesOf(rewards: readonly BackofficeReferralReward[], role: RewardRole) {
    return rewards.filter((r) => r.role === role).sort(byPromotionName);
}

/** El monto grande: el descuento va en negativo; sin monto, la etapa. */
function amountOf(r: BackofficeReferralReward): string {
    const amount = money(r.amountCents);
    if (r.status === 'NOT_APPLICABLE') return REWARD_STATUS_LABEL.NOT_APPLICABLE;
    if (!amount)
        return r.status === 'PAID' ? REWARD_STATUS_LABEL.PAID : REWARD_STATUS_LABEL.PENDING;
    return r.rewardType === 'SALE_DISCOUNT' ? `${MINUS}${amount}` : amount;
}

function paidHow(rewardType: RewardType): string {
    if (rewardType === 'SALE_DISCOUNT') return 'Descuento aplicado en su compra';
    if (rewardType === 'COUPON') return 'Cupón otorgado';
    return 'Pagado a su cuenta PCF';
}

function detailOf(r: BackofficeReferralReward): string {
    if (r.status === 'PAID') return `${paidHow(r.rewardType)} · ${formatShortDateMx(r.statusAt)}`;
    if (r.status === 'PENDING') {
        return r.role === 'REFERRER'
            ? 'Pendiente · su referido aún no cumple'
            : 'Pendiente · aún no cumple lo que pide la promo';
    }
    return 'Esta promo no le dio premio';
}

function itemOf(r: BackofficeReferralReward): RewardItem {
    return {
        key: keyOf(r),
        promotionName: r.promotionName,
        amount: amountOf(r),
        tone: r.status === 'PAID' ? 'success' : 'muted',
        detail: detailOf(r),
    };
}

/** Franja de un referido: lo que ganó el cliente por recomendarlo y lo que ganó el referido. */
export function referralRewardsOf(
    rewards?: readonly BackofficeReferralReward[],
): ReferralRewardsView {
    if (!rewards) return { kind: 'unavailable' };
    if (rewards.length === 0) return { kind: 'none' };
    const promotions = [...new Set([...rewards].sort(byPromotionName).map((r) => r.promotionName))];
    return {
        kind: 'split',
        referrer: rolesOf(rewards, 'REFERRER').map(itemOf),
        referred: rolesOf(rewards, 'REFERRED').map(itemOf),
        promotions,
    };
}

function tagTextOf(name: string, r: BackofficeReferralReward): string {
    const amount = money(r.amountCents);
    if (r.status === 'NOT_APPLICABLE') return `${name}: no aplica`;
    if (r.status === 'PENDING')
        return amount ? `${name}: ${amount} pendiente` : `${name}: pendiente`;
    if (r.rewardType === 'SALE_DISCOUNT') {
        return amount
            ? `${name}: ${MINUS}${amount} en su compra`
            : `${name}: descuento en su compra`;
    }
    if (r.rewardType === 'COUPON') return amount ? `${name}: cupón de ${amount}` : `${name}: cupón`;
    return amount ? `${name}: ${amount} a su cuenta PCF` : `${name}: pagado a su cuenta PCF`;
}

/** «Llegó recomendado por»: primero lo que ganó el cliente, luego lo que ganó quien lo recomendó. */
export function referredByRewardsOf(
    rewards: readonly BackofficeReferralReward[] | undefined,
    names: { customer: string; referrer: string },
): ReferredByRewardsView {
    if (!rewards) return { kind: 'unavailable' };
    if (rewards.length === 0) return { kind: 'none' };
    const order: [RewardRole, string][] = [
        ['REFERRED', names.customer],
        ['REFERRER', names.referrer],
    ];
    const tags = order.flatMap(([role, name]) =>
        rolesOf(rewards, role).map((r) => ({
            key: keyOf(r),
            text: capitalizeFirst(tagTextOf(name, r)),
            tone: REWARD_STATUS_TONE[r.status],
            title: `${r.promotionName} · ${REWARD_STATUS_LABEL[r.status]} el ${formatDateMx(r.statusAt)}`,
        })),
    );
    return { kind: 'tags', tags };
}

/** Totales de arriba: solo lo pagado o aplicado; sin respuesta de promociones no hay total. */
export function rewardTotalsOf(referrals: readonly BackofficeCustomerReferral[]): RewardTotals {
    if (referrals.some((r) => !r.rewards)) return { kind: 'unavailable' };
    const paid = referrals.flatMap((r) => r.rewards ?? []).filter((r) => r.status === 'PAID');
    const earned = paid.filter((r) => r.role === 'REFERRER' && PCF_REWARD_TYPES.has(r.rewardType));
    const given = paid.filter((r) => r.role === 'REFERRED');
    const sum = (list: BackofficeReferralReward[]) =>
        list.reduce((acc, r) => acc + (r.amountCents ?? 0), 0);
    return {
        kind: 'ready',
        earnedCents: sum(earned),
        earnedCount: earned.length,
        givenCents: sum(given),
        givenCount: given.length,
    };
}
